import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { strapiGetSamplingChecklistForm, type StrapiSampleInformationRow } from '../../../api/strapiCrm';
import { useAuth } from '../../../auth/AuthContext';
import {
  samplingPlanTabLabel,
  workOrderScrollableTabsSx,
  type SamplingPlanAirGroup,
} from '../../SamplingWorkOrdersPage/samplingPlanAirGroup';
import {
  WorkOrderSamplingPlanTab,
  type WorkOrderSamplingPlanTabHandle,
} from '../../SamplingWorkOrdersPage/WorkOrderDetailRoute';

type Props = {
  open: boolean;
  workOrderDocumentId: string;
  samples: StrapiSampleInformationRow[];
  quotationUsesGroups: boolean;
  confirming?: boolean;
  onClose: () => void;
  /** Called after plans for all relevant groups are validated and saved. */
  onConfirm: () => void | Promise<void>;
};

export function SurveyStartSamplingPlanModal({
  open,
  workOrderDocumentId,
  samples,
  quotationUsesGroups,
  confirming = false,
  onClose,
  onConfirm,
}: Props) {
  const { jwt } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const checklistDescription =
    'Isi form rencana sampling untuk setiap group yang relevan. Field tujuan, parameter, peralatan, cara pengambilan, dan petugas wajib diisi sebelum survey dapat dimulai.';
  const formQ = useQuery({
    queryKey: ['strapi', 'samplingChecklistForm', jwt, workOrderDocumentId],
    queryFn: () => strapiGetSamplingChecklistForm(jwt!, workOrderDocumentId),
    enabled: Boolean(open && jwt && workOrderDocumentId),
  });
  const groups = formQ.data?.groups ?? [];

  const [activeGroup, setActiveGroup] = useState<SamplingPlanAirGroup>('air');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const airRef = useRef<WorkOrderSamplingPlanTabHandle>(null);
  const nonAirRef = useRef<WorkOrderSamplingPlanTabHandle>(null);

  const resolvedActiveGroup = groups.some((group) => group.air_group === activeGroup)
    ? activeGroup
    : groups[0]?.air_group ?? 'air';

  function handleFor(group: SamplingPlanAirGroup): WorkOrderSamplingPlanTabHandle | null {
    return group === 'air' ? airRef.current : nonAirRef.current;
  }

  async function handleConfirm() {
    setValidationError(null);
    for (const group of groups) {
      const handle = handleFor(group.air_group);
      if (!handle) {
        setValidationError(`${samplingPlanTabLabel(group.air_group)}: form belum siap. Coba lagi.`);
        setActiveGroup(group.air_group);
        return;
      }
      if (handle.isBusy()) {
        setValidationError(`${samplingPlanTabLabel(group.air_group)}: masih memuat form. Tunggu sebentar.`);
        setActiveGroup(group.air_group);
        return;
      }
      const error = handle.validate();
      if (error) {
        setValidationError(error);
        setActiveGroup(group.air_group);
        return;
      }
    }

    setSaving(true);
    try {
      for (const group of groups) {
        try {
          await handleFor(group.air_group)!.save();
        } catch (error) {
          setValidationError(
            error instanceof Error
              ? error.message
              : `Gagal menyimpan ${samplingPlanTabLabel(group.air_group)}.`,
          );
          setActiveGroup(group.air_group);
          return;
        }
      }
      await onConfirm();
    } finally {
      setSaving(false);
    }
  }

  const busy = saving || confirming;
  const formReady = formQ.isSuccess;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      scroll="paper"
      data-testid="survey-start-sampling-plan-modal"
    >
      <DialogTitle>
        {isMobile ? (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ flex: '1 1 40%', minWidth: 0 }}>
              Checklist rencana pengambilan sampling
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ flex: '1 1 60%', minWidth: 0, textAlign: 'right' }}
            >
              {checklistDescription}
            </Typography>
          </Box>
        ) : (
          'Checklist rencana pengambilan sampling'
        )}
      </DialogTitle>
      <DialogContent dividers sx={isMobile ? { px: 1 } : undefined}>
        {isMobile ? null : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {checklistDescription}
          </Typography>
        )}
        {validationError ? (
          <Alert
            severity="error"
            sx={{ mb: 1.5 }}
            onClose={() => setValidationError(null)}
            data-testid="survey-start-sampling-plan-error"
          >
            {validationError}
          </Alert>
        ) : null}
        {!formReady && formQ.isPending ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : formQ.isError ? (
          <Alert severity="error">
            {formQ.error instanceof Error
              ? formQ.error.message
              : 'Gagal memuat checklist kesiapan alat.'}
          </Alert>
        ) : groups.length === 0 ? (
          <Alert severity="warning">
            {formQ.data?.warning ??
              'Work order ini belum punya jenis pengukuran dengan grouping.perencanaan_sampling (1=air, 2=udara, 3=LB3). Hubungi admin work order untuk memperbaiki master data.'}
          </Alert>
        ) : (
          <>
            <Tabs
              value={resolvedActiveGroup}
              onChange={(_, value: SamplingPlanAirGroup) => setActiveGroup(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={workOrderScrollableTabsSx}
            >
              {groups.map((group) => (
                <Tab key={group.air_group} value={group.air_group} label={group.label || samplingPlanTabLabel(group.air_group)} />
              ))}
            </Tabs>
            {groups.map((group) => (
              <Box
                key={group.air_group}
                sx={{ display: resolvedActiveGroup === group.air_group ? 'block' : 'none', pt: 1 }}
              >
                <WorkOrderSamplingPlanTab
                  ref={group.air_group === 'air' ? airRef : nonAirRef}
                  samples={samples}
                  workOrderDocumentId={workOrderDocumentId}
                  quotationUsesGroups={quotationUsesGroups}
                  airGroup={group.air_group}
                  layout="inline_form"
                  showSaveButton={false}
                  requireCompleteChecklist
                  checklistMobileCompact={isMobile}
                  checklistBootstrap={{
                    quotationDocumentId: formQ.data?.quotation_document_id ?? null,
                    planDocumentId: group.plan_document_id,
                    jenisLabels: group.jenis_labels,
                    checklist: group.checklist,
                    options: formQ.data!.options,
                  }}
                />
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions sx={isMobile ? { px: 1 } : undefined}>
        <Button onClick={onClose} disabled={busy}>
          Batal
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={busy || !formReady || groups.length === 0}
          data-testid="survey-start-sampling-plan-save"
        >
          {busy ? 'Menyimpan…' : 'Simpan checklist'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
